import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  TextField,
  Typography,
  Grid,
} from '@mui/material';
import {
  Apartment as ApartmentIcon,
  HomeWork as HomeWorkIcon,
  Villa as VillaIcon,
  Store as StoreIcon,
  UploadFile as UploadFileIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { getApiUrl } from '../utils/apiConfig';

const EMPTY_FORM = {
  titel: '',
  untertitel: '',
  adresse: '',
  kaufpreis: '',
  provision_text: '3% inkl. USt',
  card_icon: 'apartment',
  card_teaser: '',
  vermarktungsart: 'kauf',
  objektart: 'wohnung',
  objektunterart: 'etagenwohnung',
  zustand: 'gepflegt',
  lagekategorie: 'zentrumsnah',
  cover_image_url: '',
  grundriss_url: '',
  nutzflaeche: '',
  objektbeschreibung_text: '',
  ausstattung_text: '',
  lage_text: '',
  sonstige_text: '',
  stadtteil: '',
  preis_wohnung: '',
  preis_stellplatz: '',
  hausgeld_monat: '',
  wohnflaeche: '',
  zimmer: '',
  etage: '',
  terrasse_qm: '',
  baujahr: '',
  saniert_jahr: '',
  heizung: '',
  wohneinheiten: '',
  miteigentumsanteil: '',
  grundstuecksflaeche: '',
  nutzung_text: '',
  verfuegbarkeit_text: '',
  tiefgarage: false,
  keller: false,
  moebliert: false,
  beschreibung: '',
  ausstattung_liste: '',
  ea_typ: '',
  ea_endenergie: '',
  ea_traeger: '',
  ea_baujahr: '',
  ea_klasse: '',
  ea_gueltig_bis: '',
  ea_warmwasser_enthalten: false,
  imageUrlsText: '',
  exposeUrl: '',
  ist_aktiv: true,
};

const CATEGORY_PRESETS = {
  vermarktungsart: [
    { value: 'kauf', label: 'Kauf' },
    { value: 'miete', label: 'Miete' },
  ],
  objektart: [
    { value: 'wohnung', label: 'Wohnung' },
    { value: 'haus', label: 'Haus' },
    { value: 'mehrfamilienhaus', label: 'Mehrfamilienhaus' },
    { value: 'gewerbe', label: 'Gewerbe' },
  ],
  objektunterart: [
    { value: 'etagenwohnung', label: 'Etagenwohnung' },
    { value: 'erdgeschosswohnung', label: 'Erdgeschosswohnung' },
    { value: 'dachgeschosswohnung', label: 'Dachgeschosswohnung' },
    { value: 'penthouse', label: 'Penthouse' },
    { value: 'reihenhaus', label: 'Reihenhaus' },
    { value: 'einfamilienhaus', label: 'Einfamilienhaus' },
    { value: 'doppelhaushaelfte', label: 'Doppelhaushaelfte' },
    { value: 'mehrfamilienhaus', label: 'Mehrfamilienhaus' },
    { value: 'buero_praxis', label: 'Buero/Praxis' },
    { value: 'ladenflaeche', label: 'Ladenflaeche' },
  ],
  zustand: [
    { value: 'erstbezug', label: 'Erstbezug' },
    { value: 'saniert', label: 'Saniert' },
    { value: 'gepflegt', label: 'Gepflegt' },
    { value: 'renovierungsbeduerftig', label: 'Renovierungsbeduerftig' },
  ],
  lagekategorie: [
    { value: 'innenstadt', label: 'Innenstadt' },
    { value: 'zentrumsnah', label: 'Zentrumsnah' },
    { value: 'wohngebiet', label: 'Wohngebiet' },
    { value: 'ruhig_gruen', label: 'Ruhig im Gruenen' },
    { value: 'stadtrand', label: 'Stadtrand' },
  ],
};

const ICON_OPTIONS = [
  { value: 'apartment', label: 'Apartment', icon: ApartmentIcon },
  { value: 'haus', label: 'Haus', icon: HomeWorkIcon },
  { value: 'villa', label: 'Villa', icon: VillaIcon },
  { value: 'gewerbe', label: 'Gewerbe', icon: StoreIcon },
];

const renderCardIcon = (iconKey) => {
  const found = ICON_OPTIONS.find((item) => item.value === iconKey);
  const IconComponent = found?.icon || ApartmentIcon;
  return <IconComponent fontSize="small" />;
};

const toUrlList = (textValue) => {
  return String(textValue || '')
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => /^https?:\/\//i.test(line));
};

const VerkaufsangeboteManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [grundrissUploading, setGrundrissUploading] = useState(false);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFiles, setImageFiles] = useState([]);
  const [exposeFile, setExposeFile] = useState(null);
  const [openDownloadDialog, setOpenDownloadDialog] = useState(false);
  const [downloadDialogItem, setDownloadDialogItem] = useState(null);
  const [downloadEvents, setDownloadEvents] = useState([]);
  const [downloadEventsLoading, setDownloadEventsLoading] = useState(false);
  const [downloadEventsError, setDownloadEventsError] = useState('');
  const [openLeadsDialog, setOpenLeadsDialog] = useState(false);
  const [leadsDialogItem, setLeadsDialogItem] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsError, setLeadsError] = useState('');

  const currentImageUrls = useMemo(() => toUrlList(form.imageUrlsText), [form.imageUrlsText]);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const aDate = new Date(a.updated_at || a.created_at || 0).getTime();
      const bDate = new Date(b.updated_at || b.created_at || 0).getTime();
      return bDate - aDate;
    });
  }, [items]);

  const loadItems = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(getApiUrl('/api/public/properties/manage/list'));
      setItems(response.data.properties || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Verkaufsangebote konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const openCreate = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setFieldErrors({});
    setError('');
    setImageFiles([]);
    setExposeFile(null);
    setOpenDialog(true);
  };

  const openEdit = (item) => {
    const imageUrls = Array.isArray(item.fotos) ? item.fotos.map((img) => img?.url).filter(Boolean) : [];

    setEditingItem(item);
    setForm({
      titel: item.titel || '',
      untertitel: item.untertitel || '',
      adresse: item.adresse || '',
      kaufpreis: item.kaufpreis || '',
      provision_text: item.provision_text || '3% inkl. USt',
      card_icon: item.card_icon || 'apartment',
      card_teaser: item.card_teaser || '',
      vermarktungsart: item.vermarktungsart || 'kauf',
      objektart: item.objektart || 'wohnung',
      objektunterart: item.objektunterart || 'etagenwohnung',
      zustand: item.zustand || 'gepflegt',
      lagekategorie: item.lagekategorie || 'zentrumsnah',
      cover_image_url: item.cover_image_url || '',
      grundriss_url: item.grundriss_url || '',
      nutzflaeche: item.nutzflaeche || '',
      objektbeschreibung_text: item.objektbeschreibung_text || '',
      ausstattung_text: item.ausstattung_text || '',
      lage_text: item.lage_text || '',
      sonstige_text: item.sonstige_text || '',
      stadtteil: item.stadtteil || '',
      preis_wohnung: item.preis_wohnung ?? '',
      preis_stellplatz: item.preis_stellplatz ?? '',
      hausgeld_monat: item.hausgeld_monat ?? '',
      wohnflaeche: item.wohnflaeche ?? '',
      zimmer: item.zimmer ?? '',
      etage: item.etage || '',
      terrasse_qm: item.terrasse_qm ?? '',
      baujahr: item.baujahr ?? '',
      saniert_jahr: item.saniert_jahr ?? '',
      heizung: item.heizung || '',
      wohneinheiten: item.wohneinheiten ?? '',
      miteigentumsanteil: item.miteigentumsanteil || '',
      grundstuecksflaeche: item.grundstuecksflaeche ?? '',
      nutzung_text: item.nutzung_text || '',
      verfuegbarkeit_text: item.verfuegbarkeit_text || '',
      tiefgarage: item.tiefgarage === true,
      keller: item.keller === true,
      moebliert: item.moebliert === true,
      beschreibung: item.beschreibung || '',
      ausstattung_liste: Array.isArray(item.ausstattung) ? item.ausstattung.join('\n') : '',
      ea_typ: item.ea_typ || '',
      ea_endenergie: item.ea_endenergie ?? '',
      ea_traeger: item.ea_traeger || '',
      ea_baujahr: item.ea_baujahr ?? '',
      ea_klasse: item.ea_klasse || '',
      ea_gueltig_bis: item.ea_gueltig_bis ? String(item.ea_gueltig_bis).slice(0, 10) : '',
      ea_warmwasser_enthalten: item.ea_warmwasser_enthalten === true,
      imageUrlsText: imageUrls.join('\n'),
      exposeUrl: '',
      ist_aktiv: item.ist_aktiv !== false,
    });
    setFieldErrors({});
    setError('');
    setImageFiles([]);
    setExposeFile(null);
    setOpenDialog(true);
  };

  const closeDialog = () => {
    if (saving) return;
    setOpenDialog(false);
  };

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleGrundrissUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setGrundrissUploading(true);
    setError('');
    setSuccess('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const response = await axios.post(
        getApiUrl('/api/public/properties/manage-upload-grundriss'),
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const url = response?.data?.url;
      if (!url) {
        setError('Grundriss-Upload fehlgeschlagen.');
        return;
      }
      setForm((prev) => ({ ...prev, grundriss_url: url }));
      setFieldErrors((prev) => (prev.grundriss_url ? { ...prev, grundriss_url: undefined } : prev));
      setSuccess('Grundriss erfolgreich hochgeladen.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Grundriss-Upload fehlgeschlagen.');
    } finally {
      setGrundrissUploading(false);
    }
  };

  const removeImageUrl = (urlToRemove) => {
    const next = currentImageUrls.filter((url) => url !== urlToRemove);
    setForm((prev) => ({
      ...prev,
      imageUrlsText: next.join('\n'),
      cover_image_url: prev.cover_image_url === urlToRemove ? '' : prev.cover_image_url,
    }));
  };

  const submitForm = async () => {
    const nutzflaecheValue = parseFloat(String(form.nutzflaeche).replace(',', '.'));
    const nextErrors = {};

    if (!form.titel.trim()) nextErrors.titel = 'Titel ist erforderlich.';
    if (!form.adresse.trim()) nextErrors.adresse = 'Adresse ist erforderlich.';
    if (!form.provision_text.trim()) nextErrors.provision_text = 'Provision / Courtage ist erforderlich.';
    if (!form.grundriss_url.trim()) nextErrors.grundriss_url = 'Grundriss-URL ist erforderlich.';
    if (!String(form.nutzflaeche).trim() || Number.isNaN(nutzflaecheValue) || nutzflaecheValue <= 0) {
      nextErrors.nutzflaeche = 'Gültige Nutzfläche in m² (größer als 0) erforderlich.';
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      const labels = {
        titel: 'Titel',
        adresse: 'Adresse',
        provision_text: 'Provision / Courtage',
        grundriss_url: 'Grundriss-URL',
        nutzflaeche: 'Nutzfläche (m²)',
      };
      const missing = Object.keys(nextErrors).map((key) => labels[key]).join(', ');
      setError(`Bitte füllen Sie alle markierten Pflichtfelder korrekt aus: ${missing}.`);
      return;
    }

    setFieldErrors({});
    setSaving(true);
    setError('');
    setSuccess('');

    const payload = {
      titel: form.titel.trim(),
      untertitel: form.untertitel.trim(),
      adresse: form.adresse.trim(),
      kaufpreis: form.kaufpreis ? Number(form.kaufpreis) : null,
      provision_text: form.provision_text.trim(),
      card_icon: form.card_icon,
      card_teaser: form.card_teaser.trim(),
      vermarktungsart: form.vermarktungsart,
      objektart: form.objektart,
      objektunterart: form.objektunterart,
      zustand: form.zustand,
      lagekategorie: form.lagekategorie,
      cover_image_url: form.cover_image_url.trim() || null,
      grundriss_url: form.grundriss_url.trim() || null,
      nutzflaeche: nutzflaecheValue,
      objektbeschreibung_text: form.objektbeschreibung_text.trim(),
      ausstattung_text: form.ausstattung_text.trim(),
      lage_text: form.lage_text.trim(),
      sonstige_text: form.sonstige_text.trim(),
      stadtteil: form.stadtteil.trim() || null,
      preis_wohnung: form.preis_wohnung !== '' ? Number(form.preis_wohnung) : null,
      preis_stellplatz: form.preis_stellplatz !== '' ? Number(form.preis_stellplatz) : null,
      hausgeld_monat: form.hausgeld_monat !== '' ? Number(form.hausgeld_monat) : null,
      wohnflaeche: form.wohnflaeche !== '' ? Number(form.wohnflaeche) : null,
      zimmer: form.zimmer !== '' ? Number(form.zimmer) : null,
      etage: form.etage.trim() || null,
      terrasse_qm: form.terrasse_qm !== '' ? Number(form.terrasse_qm) : null,
      baujahr: form.baujahr !== '' ? parseInt(form.baujahr, 10) : null,
      saniert_jahr: form.saniert_jahr !== '' ? parseInt(form.saniert_jahr, 10) : null,
      heizung: form.heizung.trim() || null,
      wohneinheiten: form.wohneinheiten !== '' ? parseInt(form.wohneinheiten, 10) : null,
      miteigentumsanteil: form.miteigentumsanteil.trim() || null,
      grundstuecksflaeche: form.grundstuecksflaeche !== '' ? Number(form.grundstuecksflaeche) : null,
      nutzung_text: form.nutzung_text.trim() || null,
      verfuegbarkeit_text: form.verfuegbarkeit_text.trim() || null,
      tiefgarage: Boolean(form.tiefgarage),
      keller: Boolean(form.keller),
      moebliert: Boolean(form.moebliert),
      beschreibung: form.beschreibung.trim() || null,
      ausstattung: form.ausstattung_liste
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean),
      ea_typ: form.ea_typ.trim() || null,
      ea_endenergie: form.ea_endenergie !== '' ? Number(form.ea_endenergie) : null,
      ea_traeger: form.ea_traeger.trim() || null,
      ea_baujahr: form.ea_baujahr !== '' ? parseInt(form.ea_baujahr, 10) : null,
      ea_klasse: form.ea_klasse.trim() || null,
      ea_gueltig_bis: form.ea_gueltig_bis || null,
      ea_warmwasser_enthalten: Boolean(form.ea_warmwasser_enthalten),
      ist_aktiv: form.ist_aktiv,
      fotos: form.imageUrlsText,
    };

    try {
      let slug = editingItem?.slug;

      if (editingItem) {
        await axios.put(getApiUrl(`/api/public/properties/${editingItem.slug}/manage`), payload);
      } else {
        const response = await axios.post(getApiUrl('/api/public/properties/manage'), payload);
        slug = response.data?.property?.slug;
      }

      const uploadedImageUrls = [];
      for (const file of imageFiles) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('caption', file.name);

        const uploadResponse = await axios.post(
          getApiUrl(`/api/public/properties/${slug}/manage-upload-image`),
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (uploadResponse?.data?.image?.url) {
          uploadedImageUrls.push(uploadResponse.data.image.url);
        }
      }

      if (uploadedImageUrls.length > 0) {
        const existingUrls = toUrlList(form.imageUrlsText);
        const mergedUrls = Array.from(new Set([...existingUrls, ...uploadedImageUrls]));
        const nextCover = payload.cover_image_url || mergedUrls[0] || null;

        await axios.put(getApiUrl(`/api/public/properties/${slug}/manage`), {
          ...payload,
          cover_image_url: nextCover,
          fotos: mergedUrls.join('\n'),
        });
      }

      if (slug && exposeFile) {
        const fdExpose = new FormData();
        fdExpose.append('file', exposeFile);
        fdExpose.append('label', 'Expose');
        fdExpose.append('gated', 'true');
        await axios.post(getApiUrl(`/api/public/properties/${slug}/manage-upload-expose`), fdExpose, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      if (slug && form.exposeUrl && /^https?:\/\//i.test(form.exposeUrl.trim())) {
        await axios.put(getApiUrl(`/api/public/properties/${slug}/manage-expose`), {
          source_url: form.exposeUrl.trim(),
          label: 'Expose',
          gated: true,
        });
      }

      setSuccess(editingItem ? 'Verkaufsangebot aktualisiert.' : 'Verkaufsangebot angelegt.');
      setOpenDialog(false);
      await loadItems();
    } catch (err) {
      setError(err?.response?.data?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (item) => {
    const confirmed = window.confirm(`Soll das Angebot "${item.titel}" wirklich geloescht werden?`);
    if (!confirmed) return;

    setError('');
    setSuccess('');
    try {
      await axios.delete(getApiUrl(`/api/public/properties/${item.slug}/manage`));
      setSuccess('Verkaufsangebot geloescht.');
      await loadItems();
    } catch (err) {
      setError(err?.response?.data?.message || 'Loeschen fehlgeschlagen.');
    }
  };

  const openDownloadHistory = async (item) => {
    setDownloadDialogItem(item);
    setOpenDownloadDialog(true);
    setDownloadEvents([]);
    setDownloadEventsError('');
    setDownloadEventsLoading(true);

    try {
      const response = await axios.get(getApiUrl(`/api/public/properties/${item.slug}/manage-download-events`));
      setDownloadEvents(response.data?.events || []);
    } catch (err) {
      setDownloadEventsError(err?.response?.data?.message || 'Download-Historie konnte nicht geladen werden.');
    } finally {
      setDownloadEventsLoading(false);
    }
  };

  const openLeadsView = async (item) => {
    setLeadsDialogItem(item);
    setOpenLeadsDialog(true);
    setLeads([]);
    setLeadsError('');
    setLeadsLoading(true);

    try {
      const response = await axios.get(getApiUrl(`/api/public/properties/${item.slug}/manage`));
      setLeads(response.data?.leads || []);
    } catch (err) {
      setLeadsError(err?.response?.data?.message || 'Leads konnten nicht geladen werden.');
    } finally {
      setLeadsLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1.5}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a365d' }}>Verkaufsangebote</Typography>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              Angebote im Loginbereich verwalten, Expose hinterlegen und oeffentliche Detailseiten steuern.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} sx={{ textTransform: 'none', fontWeight: 700 }} onClick={openCreate}>
            Neues Angebot
          </Button>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}

        {loading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2}>
            {sortedItems.map((item) => (
              <Grid item xs={12} md={6} key={item.id}>
                <Card sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
                  <CardContent>
                    <Stack spacing={1.2}>
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Chip icon={renderCardIcon(item.card_icon)} label={item.card_icon || 'apartment'} size="small" variant="outlined" />
                          <Chip label={item.ist_aktiv ? 'Aktiv' : 'Inaktiv'} color={item.ist_aktiv ? 'success' : 'default'} size="small" />
                        </Stack>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>{item.slug}</Typography>
                      </Stack>

                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a' }}>{item.titel}</Typography>
                      {item.untertitel && <Typography variant="body2" sx={{ color: '#475569' }}>{item.untertitel}</Typography>}
                      {item.card_teaser && <Typography variant="body2" sx={{ color: '#64748b' }}>{item.card_teaser}</Typography>}
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {item.vermarktungsart && <Chip size="small" label={item.vermarktungsart} />}
                        {item.objektart && <Chip size="small" label={item.objektart} variant="outlined" />}
                        {item.objektunterart && <Chip size="small" label={item.objektunterart} variant="outlined" />}
                        {item.zustand && <Chip size="small" label={item.zustand} variant="outlined" />}
                        {item.lagekategorie && <Chip size="small" label={item.lagekategorie} variant="outlined" />}
                      </Stack>
                      {item.cover_image_url && (
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Startbild gesetzt
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ color: '#334155' }}>{item.adresse}</Typography>
                      <Typography variant="subtitle2" sx={{ color: '#0891b2', fontWeight: 800 }}>
                        {item.kaufpreis ? `${Number(item.kaufpreis).toLocaleString('de-DE')} EUR` : 'Kaufpreis auf Anfrage'}
                      </Typography>

                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button size="small" startIcon={<OpenInNewIcon />} sx={{ textTransform: 'none' }} onClick={() => window.open(`/objekt/${item.slug}`, '_blank')}>
                          Objektseite
                        </Button>
                        <Button size="small" sx={{ textTransform: 'none' }} onClick={() => openDownloadHistory(item)}>
                          Download-Historie
                        </Button>
                        <Button size="small" sx={{ textTransform: 'none' }} onClick={() => openLeadsView(item)}>
                          Leads
                        </Button>
                        <Button size="small" startIcon={<EditIcon />} sx={{ textTransform: 'none' }} onClick={() => openEdit(item)}>
                          Bearbeiten
                        </Button>
                        <Button size="small" color="error" startIcon={<DeleteIcon />} sx={{ textTransform: 'none' }} onClick={() => deleteItem(item)}>
                          Loeschen
                        </Button>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>

      <Dialog open={openDialog} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Angebot bearbeiten' : 'Neues Angebot'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Titel" value={form.titel} onChange={handleChange('titel')} required fullWidth error={Boolean(fieldErrors.titel)} helperText={fieldErrors.titel || ''} />
            <TextField label="Untertitel" value={form.untertitel} onChange={handleChange('untertitel')} fullWidth />
            <TextField label="Adresse" value={form.adresse} onChange={handleChange('adresse')} required fullWidth error={Boolean(fieldErrors.adresse)} helperText={fieldErrors.adresse || ''} />
            <TextField label="Kaufpreis (EUR)" type="number" value={form.kaufpreis} onChange={handleChange('kaufpreis')} fullWidth />
            <TextField
              label="Provision / Courtage"
              value={form.provision_text}
              onChange={handleChange('provision_text')}
              required
              fullWidth
              error={Boolean(fieldErrors.provision_text)}
              placeholder="z. B. 3,57 % inkl. 19 % USt. vom Kaufpreis, fällig bei Kaufvertragsabschluss"
              helperText={fieldErrors.provision_text || 'Pflichtangabe – wird auf der Objektseite und in der Maklereinwilligung angezeigt.'}
            />

            <FormControl fullWidth>
              <InputLabel id="vermarktungsart-label">Vermarktungsart</InputLabel>
              <Select
                labelId="vermarktungsart-label"
                value={form.vermarktungsart}
                label="Vermarktungsart"
                onChange={handleChange('vermarktungsart')}
              >
                {CATEGORY_PRESETS.vermarktungsart.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="objektart-label">Objektart</InputLabel>
              <Select
                labelId="objektart-label"
                value={form.objektart}
                label="Objektart"
                onChange={handleChange('objektart')}
              >
                {CATEGORY_PRESETS.objektart.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="objektunterart-label">Objektunterart</InputLabel>
              <Select
                labelId="objektunterart-label"
                value={form.objektunterart}
                label="Objektunterart"
                onChange={handleChange('objektunterart')}
              >
                {CATEGORY_PRESETS.objektunterart.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="zustand-label">Zustand</InputLabel>
              <Select
                labelId="zustand-label"
                value={form.zustand}
                label="Zustand"
                onChange={handleChange('zustand')}
              >
                {CATEGORY_PRESETS.zustand.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="lagekategorie-label">Lagekategorie</InputLabel>
              <Select
                labelId="lagekategorie-label"
                value={form.lagekategorie}
                label="Lagekategorie"
                onChange={handleChange('lagekategorie')}
              >
                {CATEGORY_PRESETS.lagekategorie.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1a365d', mt: 1 }}>
              Preise &amp; Flächen
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Preis Wohnung (EUR)" type="number" value={form.preis_wohnung} onChange={handleChange('preis_wohnung')} fullWidth inputProps={{ step: '0.01', min: '0' }} />
              <TextField label="Preis Stellplatz (EUR)" type="number" value={form.preis_stellplatz} onChange={handleChange('preis_stellplatz')} fullWidth inputProps={{ step: '0.01', min: '0' }} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Hausgeld / Monat (EUR)" type="number" value={form.hausgeld_monat} onChange={handleChange('hausgeld_monat')} fullWidth inputProps={{ step: '0.01', min: '0' }} />
              <TextField label="Wohnfläche (m²)" type="number" value={form.wohnflaeche} onChange={handleChange('wohnflaeche')} fullWidth inputProps={{ step: '0.01', min: '0' }} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Zimmer" type="number" value={form.zimmer} onChange={handleChange('zimmer')} fullWidth inputProps={{ step: '0.5', min: '0' }} />
              <TextField label="Terrasse / Balkon (m²)" type="number" value={form.terrasse_qm} onChange={handleChange('terrasse_qm')} fullWidth inputProps={{ step: '0.01', min: '0' }} />
            </Stack>

            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1a365d', mt: 1 }}>
              Objektdetails
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Stadtteil" value={form.stadtteil} onChange={handleChange('stadtteil')} fullWidth />
              <TextField label="Etage" value={form.etage} onChange={handleChange('etage')} fullWidth placeholder="z. B. 2. OG von 4" />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Baujahr" type="number" value={form.baujahr} onChange={handleChange('baujahr')} fullWidth inputProps={{ min: '1800', max: '2100', step: '1' }} />
              <TextField label="Saniert (Jahr)" type="number" value={form.saniert_jahr} onChange={handleChange('saniert_jahr')} fullWidth inputProps={{ min: '1800', max: '2100', step: '1' }} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Heizung" value={form.heizung} onChange={handleChange('heizung')} fullWidth placeholder="z. B. Gas-Zentralheizung" />
              <TextField label="Wohneinheiten" type="number" value={form.wohneinheiten} onChange={handleChange('wohneinheiten')} fullWidth inputProps={{ min: '0', step: '1' }} />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Miteigentumsanteil" value={form.miteigentumsanteil} onChange={handleChange('miteigentumsanteil')} fullWidth placeholder="z. B. 125/1000" helperText="Anteil am Gesamtgrundstück, z. B. 125/1000" />
              <TextField label="Grundstücksfläche (gesamt)" type="number" value={form.grundstuecksflaeche} onChange={handleChange('grundstuecksflaeche')} fullWidth inputProps={{ min: '0', step: '0.01' }} InputProps={{ endAdornment: <InputAdornment position="end">m²</InputAdornment> }} helperText="Gesamtfläche des Grundstücks. Mit dem Miteigentumsanteil wird der anteilige Grundstücksanteil für Käufer berechnet." />
            </Stack>
            <TextField label="Nutzung (Text)" value={form.nutzung_text} onChange={handleChange('nutzung_text')} fullWidth multiline minRows={2} />
            <TextField label="Verfügbarkeit (Text)" value={form.verfuegbarkeit_text} onChange={handleChange('verfuegbarkeit_text')} fullWidth placeholder="z. B. ab sofort / nach Vereinbarung" />
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <FormControlLabel control={<Switch checked={form.tiefgarage} onChange={handleChange('tiefgarage')} />} label="Tiefgarage" />
              <FormControlLabel control={<Switch checked={form.keller} onChange={handleChange('keller')} />} label="Keller" />
              <FormControlLabel control={<Switch checked={form.moebliert} onChange={handleChange('moebliert')} />} label="Möbliert" />
            </Stack>

            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1a365d', mt: 1 }}>
              Beschreibung &amp; Ausstattung
            </Typography>
            <TextField label="Beschreibung (Kurztext)" value={form.beschreibung} onChange={handleChange('beschreibung')} fullWidth multiline minRows={3} />
            <TextField
              label="Ausstattungsmerkmale (eines pro Zeile)"
              value={form.ausstattung_liste}
              onChange={handleChange('ausstattung_liste')}
              fullWidth
              multiline
              minRows={3}
              helperText="Je Zeile ein Merkmal, z. B. Einbauküche, Fußbodenheizung, Aufzug"
            />

            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1a365d', mt: 1 }}>
              Energieausweis
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Energieausweis-Typ" value={form.ea_typ} onChange={handleChange('ea_typ')} fullWidth placeholder="Verbrauchs- oder Bedarfsausweis" />
              <TextField label="Energieklasse" value={form.ea_klasse} onChange={handleChange('ea_klasse')} fullWidth placeholder="z. B. C" />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Endenergiebedarf (kWh/m²·a)" type="number" value={form.ea_endenergie} onChange={handleChange('ea_endenergie')} fullWidth inputProps={{ step: '0.1', min: '0' }} />
              <TextField label="Energieträger" value={form.ea_traeger} onChange={handleChange('ea_traeger')} fullWidth placeholder="z. B. Erdgas" />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Energieausweis Baujahr" type="number" value={form.ea_baujahr} onChange={handleChange('ea_baujahr')} fullWidth inputProps={{ min: '1800', max: '2100', step: '1' }} />
              <TextField
                label="Energieausweis gültig bis"
                type="date"
                value={form.ea_gueltig_bis}
                onChange={handleChange('ea_gueltig_bis')}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
            <FormControlLabel control={<Switch checked={form.ea_warmwasser_enthalten} onChange={handleChange('ea_warmwasser_enthalten')} />} label="Warmwasser im Energiebedarf enthalten" />

            <TextField
              label="Objektbeschreibung"
              value={form.objektbeschreibung_text}
              onChange={handleChange('objektbeschreibung_text')}
              fullWidth
              multiline
              minRows={4}
            />

            <TextField
              label="Ausstattung (Text)"
              value={form.ausstattung_text}
              onChange={handleChange('ausstattung_text')}
              fullWidth
              multiline
              minRows={3}
            />

            <TextField
              label="Lage"
              value={form.lage_text}
              onChange={handleChange('lage_text')}
              fullWidth
              multiline
              minRows={3}
            />

            <TextField
              label="Sonstige"
              value={form.sonstige_text}
              onChange={handleChange('sonstige_text')}
              fullWidth
              multiline
              minRows={3}
            />

            <FormControl fullWidth>
              <InputLabel id="card-icon-label">Karten-Icon</InputLabel>
              <Select
                labelId="card-icon-label"
                value={form.card_icon}
                label="Karten-Icon"
                onChange={handleChange('card_icon')}
              >
                {ICON_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Karten-Teaser"
              value={form.card_teaser}
              onChange={handleChange('card_teaser')}
              fullWidth
              multiline
              minRows={2}
            />

            <TextField
              label="Bild-URLs (eine URL pro Zeile)"
              value={form.imageUrlsText}
              onChange={handleChange('imageUrlsText')}
              fullWidth
              multiline
              minRows={3}
            />

            {currentImageUrls.length > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {currentImageUrls.map((url) => (
                  <Chip
                    key={url}
                    label={url.length > 38 ? `${url.slice(0, 38)}...` : url}
                    onDelete={() => removeImageUrl(url)}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Stack>
            )}

            <FormControl fullWidth>
              <InputLabel id="cover-image-label">Startbild</InputLabel>
              <Select
                labelId="cover-image-label"
                value={form.cover_image_url}
                label="Startbild"
                onChange={handleChange('cover_image_url')}
              >
                <MenuItem value="">Kein Startbild gesetzt</MenuItem>
                {currentImageUrls.map((url) => (
                  <MenuItem key={url} value={url}>{url}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="text"
              sx={{ width: 'fit-content', textTransform: 'none' }}
              onClick={() => {
                if (currentImageUrls.length > 0) {
                  setForm((prev) => ({ ...prev, cover_image_url: currentImageUrls[0] }));
                }
              }}
            >
              Erstes Bild als Startbild setzen
            </Button>

            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} sx={{ textTransform: 'none' }}>
              Bilder hochladen
              <input
                hidden
                type="file"
                accept="image/*"
                multiple
                onChange={(event) => {
                  const files = Array.from(event.target.files || []);
                  setImageFiles(files);
                }}
              />
            </Button>
            {imageFiles.length > 0 && (
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                {imageFiles.length} Bilddatei(en) ausgewaehlt. Upload erfolgt beim Speichern.
              </Typography>
            )}

            <TextField
              label="Grundriss URL *"
              value={form.grundriss_url}
              onChange={handleChange('grundriss_url')}
              fullWidth
              required
              error={Boolean(fieldErrors.grundriss_url)}
              placeholder="https://.../grundriss.pdf"
              helperText={fieldErrors.grundriss_url || 'Pflichtfeld - URL zum Grundriss-Dokument (oder Datei hochladen)'}
            />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button
                variant="outlined"
                component="label"
                startIcon={grundrissUploading ? <CircularProgress size={16} /> : <UploadFileIcon />}
                disabled={grundrissUploading}
                sx={{ textTransform: 'none', width: 'fit-content' }}
              >
                {grundrissUploading ? 'Wird hochgeladen...' : 'Grundriss hochladen (JPG/PNG/PDF)'}
                <input
                  hidden
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf,image/*,application/pdf"
                  onChange={handleGrundrissUpload}
                />
              </Button>
              {form.grundriss_url && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  sx={{ textTransform: 'none' }}
                  onClick={() => window.open(form.grundriss_url, '_blank', 'noopener')}
                >
                  Aktuellen Grundriss ansehen
                </Button>
              )}
            </Stack>

            <TextField
              label="Nutzflaeche (m²) *"
              value={form.nutzflaeche}
              onChange={handleChange('nutzflaeche')}
              fullWidth
              required
              error={Boolean(fieldErrors.nutzflaeche)}
              type="number"
              inputProps={{ step: '0.01', min: '0' }}
              helperText={fieldErrors.nutzflaeche || 'Pflichtfeld - Nutzfläche in Quadratmetern'}
            />

            <TextField
              label="Expose-PDF URL (optional)"
              value={form.exposeUrl}
              onChange={handleChange('exposeUrl')}
              fullWidth
              placeholder="https://.../expose.pdf"
            />

            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} sx={{ textTransform: 'none' }}>
              Expose-PDF hochladen
              <input
                hidden
                type="file"
                accept="application/pdf"
                onChange={(event) => {
                  const file = (event.target.files || [])[0] || null;
                  setExposeFile(file);
                }}
              />
            </Button>
            {exposeFile && (
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                Ausgewaehltes Expose: {exposeFile.name}
              </Typography>
            )}

            <FormControlLabel
              control={<Switch checked={form.ist_aktiv} onChange={handleChange('ist_aktiv')} />}
              label="Angebot aktiv schalten"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>Abbrechen</Button>
          <Button variant="contained" onClick={submitForm} disabled={saving}>
            {saving ? 'Speichern...' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDownloadDialog}
        onClose={() => setOpenDownloadDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Download-Historie
          {downloadDialogItem?.titel ? ` - ${downloadDialogItem.titel}` : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {downloadEventsError && <Alert severity="error">{downloadEventsError}</Alert>}

            {downloadEventsLoading ? (
              <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
                <CircularProgress size={26} />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Zeitstempel</TableCell>
                      <TableCell>Dokument</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Absicht</TableCell>
                      <TableCell>Finanzierung</TableCell>
                      <TableCell>Zeithorizont</TableCell>
                      <TableCell>E-Mail</TableCell>
                      <TableCell>Telefon</TableCell>
                      <TableCell>IP</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {downloadEvents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Noch keine Downloads protokolliert.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      downloadEvents.map((event) => (
                        <TableRow key={event.id}>
                          <TableCell>
                            {event.downloaded_at
                              ? new Date(event.downloaded_at).toLocaleString('de-DE')
                              : '-'}
                          </TableCell>
                          <TableCell>{event.document_label || '-'}</TableCell>
                          <TableCell>{event.lead_name || '-'}</TableCell>
                          <TableCell>{event.lead_absicht || '-'}</TableCell>
                          <TableCell>{event.lead_finanzierung || '-'}</TableCell>
                          <TableCell>{event.lead_zeithorizont || '-'}</TableCell>
                          <TableCell>{event.lead_email || '-'}</TableCell>
                          <TableCell>{event.lead_telefon || '-'}</TableCell>
                          <TableCell>{event.ip_address || '-'}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDownloadDialog(false)}>Schliessen</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openLeadsDialog}
        onClose={() => setOpenLeadsDialog(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Anfragen (Leads)
          {leadsDialogItem?.titel ? ` - ${leadsDialogItem.titel}` : ''}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {leadsError && <Alert severity="error">{leadsError}</Alert>}

            {leadsLoading ? (
              <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
                <CircularProgress size={26} />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>E-Mail</TableCell>
                      <TableCell>Telefon</TableCell>
                      <TableCell>Absicht</TableCell>
                      <TableCell>Finanzierung</TableCell>
                      <TableCell>Zeithorizont</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Vorzeitiger Beginn</TableCell>
                      <TableCell>Widerruf erhalten</TableCell>
                      <TableCell>Erstellt</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leads.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            Noch keine Anfragen eingegangen.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      leads.map((lead) => (
                        <TableRow key={lead.id}>
                          <TableCell>{lead.name || '-'}</TableCell>
                          <TableCell>{lead.email || '-'}</TableCell>
                          <TableCell>{lead.telefon || '-'}</TableCell>
                          <TableCell>{lead.absicht || '-'}</TableCell>
                          <TableCell>{lead.finanzierung || '-'}</TableCell>
                          <TableCell>{lead.zeithorizont || '-'}</TableCell>
                          <TableCell>{lead.score || '-'}</TableCell>
                          <TableCell>
                            {lead.vorzeitiger_beginn_consent ? (
                              <Chip label="✓ Bestätigt" size="small" color="success" />
                            ) : (
                              <Chip label="Nicht bestätigt" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {lead.widerrufsbelehrung_consent ? (
                              <Chip label="✓ Bestätigt" size="small" color="success" />
                            ) : (
                              <Chip label="Nicht bestätigt" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {lead.created_at
                              ? new Date(lead.created_at).toLocaleString('de-DE')
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLeadsDialog(false)}>Schliessen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VerkaufsangeboteManagement;
